import { Component, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Import services
import { SpotService } from 'src/app/services/spot.service';
import { WalletService } from 'src/app/services/wallet.service';

// Import facades
import { PairFacade } from '../../store/pair';
import { CurrencyFacade } from '../../store/currency';
import { MarketPriceFacade } from 'src/app/store/marketprice';
import { TradeAssetFacade } from 'src/app/store/trade-asset';

// Import utilities
import isEmpty from 'src/app/lib/isEmpty';
import { convert, longDecimals, toFixedDown, truncateDecimals } from 'src/app/lib/roundOf';
import { Cookies } from 'src/app/lib/cookies';

// Import config
import { spotSocket } from 'src/app/app.module';

@Component({
  selector: 'app-market',
  templateUrl: './market.component.html',
  styleUrls: ['./market.component.css']
})
export class MarketComponent implements OnDestroy {
  
  @Input() tikerRoot: any;

  // Utility functions
  toFixedDown = toFixedDown;
  truncateDecimals = truncateDecimals;
  convert = convert;
  longDecimals = longDecimals;

  pairs: { [key: string]: any[] } = {};  // Stores pair data grouped by currency
  sortedPairs: { [key: string]: any[] } = {}; // Stores sorted pair data
  currencies: string[] = []; 
  activeCurrency: string = '';
  currencyList: any[] = [];
  searchPair: string = '';
  sortOrder: { [key: string]: boolean } = {}; // Tracks sorting order for each currency
    // Pagination
    currentPage: number = 1;
    itemsPerPage: number = 10;

  constructor(
    private service: SpotService,
    private walletService: WalletService,
    private pairFacade: PairFacade,
    private currencyFacade: CurrencyFacade,
    private tradeAssetFacade: TradeAssetFacade,
    private router: Router,
    private cookies: Cookies,
    private spotSocket: spotSocket,
    private marketPriceFacade: MarketPriceFacade,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.initializeData();
    this.spotSocket.on('marketPrice', this.marketPriceWs.bind(this));
  }

  ngOnDestroy() {
    this.spotSocket.off('marketPrice');
  }

  private initializeData() {
    this.fetchCurrencies();
    this.fetchPairs();
  }

  private fetchCurrencies() {
    this.currencyFacade.currency$.subscribe({
      next: (data: any) => {
        this.currencyList = data;
      },
      error: (err: any) => console.error('Error fetching currencies:', err),
    });
  }

  private fetchPairs() {
    this.service.getPairList().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.pairFacade.setPair(response.result);
          this.processPairData(response.result);
        }
      },
      error: (err: any) => console.error('Error fetching pairs:', err),
    });
  }

  private processPairData(data: any[]) {
    this.pairs = data.reduce((acc: any, curr: any) => {
      if (!acc[curr.secondCurrencySymbol]) {
        acc[curr.secondCurrencySymbol] = [];
        this.currencies.push(curr.secondCurrencySymbol);
      }
      acc[curr.secondCurrencySymbol].push(curr);
      return acc;
    }, {});

    // Set default active currency & sort data initially
    this.activeCurrency = this.currencies[0] || '';
    this.sortedPairs = { ...this.pairs };
  }

  getCurrecyImage(currency: string) {
    const curr = this.currencyList.find((el: any) => el.coin === currency);
    return curr?.image || "";
  }

  fetchAssetByCurrency(spotPairId: string, type: 'firstCurrency' | 'secondCurrency') {
    if (!this.cookies.isLogin()) return;

    this.walletService.getAssetByCurrency(spotPairId).subscribe({
      next: (response: any) => {
        if (response.success) {
          if (type === 'firstCurrency') {
            this.tradeAssetFacade.setFirstCurrency(response.result);
          } else if (type === 'secondCurrency') {
            this.tradeAssetFacade.setSecondCurrency(response.result);
          }
        }
      },
      error: (err: any) => console.error('Error fetching assets:', err),
    });
  }

  marketPriceWs(result: any) {
    for (const secondCur of this.currencies) {
      const index = this.pairs[secondCur]?.findIndex((el: any) => el._id === result.pairId);
      if (index !== -1) {
        this.pairs[secondCur][index] = {
          ...this.pairs[secondCur][index],
          markPrice: result.data.markPrice,
          change: result.data.change,
          high: result.data.high,
          firstVolume: result.data.firstVolume,
        };
      }
    }
  }

  handleSearchPair(event: any) {
    this.searchPair = event.target.value.toUpperCase().replace('/', '');
  }

  // Sorting function for table
  sortData(field: string) {
    if (!this.activeCurrency) return;

    const order = this.sortOrder[this.activeCurrency] ?? true; // Default to ascending

    this.sortedPairs[this.activeCurrency] = [...this.pairs[this.activeCurrency]].sort((a, b) => {
      if (typeof a[field] === 'string') {
        return order ? a[field].localeCompare(b[field]) : b[field].localeCompare(a[field]);
      } else {
        return order ? a[field] - b[field] : b[field] - a[field];
      }
    });

    this.sortOrder[this.activeCurrency] = !order; // Toggle order
  }
}
